'use client';

import { automationId, OnboardingAutomationIds } from '@terra/shared-utils';
import { Box } from '@terra/ui-styled-system';
import { useTranslations } from 'next-intl';
import { Field } from '../../../../components/field';
import { useDraftOnOpen } from '../../../application-general/useDraftOnOpen';
import { SingleDatePicker } from '../../../inputs/SingleDatePicker';
import { SettingsEditDialog } from '../../SettingsEditDialog';
import {
  toPentestReportDate,
  toPentestReportDateFieldValue,
} from './pentest-report-date.utils';

export type PentestReportDateEditDialogProps = {
  open: boolean;
  nextPentestReportDate?: string | Date | null;
  isSaving?: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (nextPentestReportDate: Date | null) => Promise<boolean>;
};

export function PentestReportDateEditDialog({
  open,
  nextPentestReportDate,
  isSaving,
  onOpenChange,
  onSave,
}: PentestReportDateEditDialogProps) {
  const t = useTranslations('designSystem.automation.pentestReport');
  const tCommon = useTranslations('designSystem.common');
  const [draft, setDraft] = useDraftOnOpen({
    open,
    value: toPentestReportDate(nextPentestReportDate),
  });
  const draftFieldValue = toPentestReportDateFieldValue(draft);

  const handleSubmit = async () => {
    const saved = await onSave(draft);

    if (saved) {
      onOpenChange(false);
    }
  };

  return (
    <SettingsEditDialog
      open={open}
      title={t('dateLabel')}
      onOpenChange={onOpenChange}
      onSubmit={handleSubmit}
      loading={isSaving}
      isSubmitDisabled={
        draftFieldValue === toPentestReportDateFieldValue(nextPentestReportDate)
      }
      submitText={tCommon('saveChanges')}
      contentAutomationId={OnboardingAutomationIds.pentestReportDialog}
      submitAutomationId={OnboardingAutomationIds.pentestReportSave}
    >
      <Field.Root w="full">
        <Field.Label>{t('dateLabel')}</Field.Label>
        <Box
          w="full"
          {...automationId(OnboardingAutomationIds.pentestReportDateInput)}
        >
          <SingleDatePicker
            value={draftFieldValue}
            onChange={(nextDateString) =>
              setDraft(nextDateString ? new Date(nextDateString) : null)
            }
            placeholder={t('datePlaceholder')}
          />
        </Box>
      </Field.Root>
    </SettingsEditDialog>
  );
}
